import React, { useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, Card, CardContent, Checkbox, CircularProgress, FormControlLabel, Stack, Typography } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { LoadingButton } from "@mui/lab";
import { useLocation, useNavigate } from "react-router-dom";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import Logo from "app/shared/Logo";
import apiAccessServices from "app/services/api-access-services";
import { rememberReturnPath } from "app/utils/afterLogin";
import { getTenantBranding } from "app/utils/tenantBranding";

// «¿Autorizas a Claude…?»: la pantalla a la que Claude.ai, ChatGPT u otro cliente mandan al usuario para conectar el
// MCP (OAuth). Sin sesión pasa por el login y vuelve. Al autorizar (o cancelar) el backend crea el código y el
// navegador vuelve al cliente.
const OAuthAuthorize = () => {
    const { authToken, authUser } = useJumboAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const params = useMemo(() => Object.fromEntries(new URLSearchParams(location.search)), [location.search]);
    const [consent, setConsent] = useState(null);
    const [error, setError] = useState(null);
    const [answering, setAnswering] = useState(null);
    // Crear y editar se pide aparte y nunca va marcado de antemano, aunque el cliente lo pida.
    const [grantWrite, setGrantWrite] = useState(false);

    useEffect(() => {
        if (!authToken) {
            rememberReturnPath(`${location.pathname}${location.search}`);
            navigate("/auth/login", { replace: true });
            return;
        }
        if (!authUser) return; // se está cargando la sesión
        apiAccessServices.getAuthorization(params)
            .then(setConsent)
            .catch((requestError) => setError(requestError?.response?.data?.error || "No se pudo leer el pedido de autorización."));
    }, [authToken, authUser]); // eslint-disable-line react-hooks/exhaustive-deps

    const answer = async (approve) => {
        setAnswering(approve ? "approve" : "deny");
        try {
            const { redirect_to: redirectTo } = await apiAccessServices.answerAuthorization(params, approve, approve && grantWrite);
            window.location.href = redirectTo;
        } catch (requestError) {
            setError(requestError?.response?.data?.error || "No se pudo completar la autorización.");
            setAnswering(null);
        }
    };

    const mcpCompanies = consent?.companies?.mcp || [];
    const apiCompanies = consent?.companies?.api || [];
    const companies = mcpCompanies.length ? mcpCompanies : apiCompanies;

    return (
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", p: 3 }}>
            <Logo sx={{ mb: 3 }} logo_url={getTenantBranding().authLogoUrl} img_style={{ maxHeight: "48px" }} />
            <Card sx={{ width: "100%", maxWidth: 460 }}>
                <CardContent sx={{ p: 3 }}>
                    {!consent && !error && <Box sx={{ py: 4, textAlign: "center" }}><CircularProgress /></Box>}
                    {!!error && <Alert severity="error">{error}</Alert>}
                    {consent && (
                        <Stack spacing={2}>
                            <Typography variant="h3">Autorizar acceso</Typography>
                            <Typography variant="body1">
                                <strong>{consent.client.name}</strong> quiere conectarse a tu cuenta de Xpit Connect ({consent.user.email}).
                            </Typography>
                            <Box>
                                <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>Podrá:</Typography>
                                {consent.scopes.map((scope) => (
                                    <Stack key={scope.scope} direction="row" spacing={1} alignItems="center">
                                        <CheckCircleOutlineIcon color="success" fontSize="small" />
                                        <Typography variant="body2">{scope.label}</Typography>
                                    </Stack>
                                ))}
                                {consent.write?.available && (
                                    <FormControlLabel
                                        sx={{ mt: 0.5, alignItems: "flex-start" }}
                                        control={<Checkbox size="small" sx={{ pt: 0.25 }} checked={grantWrite} onChange={(event) => setGrantWrite(event.target.checked)} />}
                                        label={(
                                            <Typography variant="body2">
                                                {consent.write.label}
                                                <Typography component="span" variant="caption" color="text.secondary" display="block">
                                                    Opcional. Un cambio que baje la protección de una regla queda propuesto hasta que alguien lo apruebe en Automatizaciones.
                                                </Typography>
                                            </Typography>
                                        )}
                                    />
                                )}
                            </Box>
                            {companies.length ? (
                                <Typography variant="body2" color="text.secondary">
                                    De {companies.length === 1 ? "la empresa" : "las empresas"} {companies.map((company) => company.name).join(", ")}, solo
                                    lo que tu perfil permite. {grantWrite ? "No podrá borrar nada ni operar equipos" : "No podrá modificar nada"}, y puedes
                                    quitarle el acceso cuando quieras desde «Mi perfil».
                                </Typography>
                            ) : (
                                <Alert severity="warning">
                                    Ninguna de tus empresas tiene activo el MCP ni la API. Pídeselo a tu administrador o a Xpit.
                                </Alert>
                            )}
                            <Typography variant="caption" color="text.secondary">Al terminar volverás a {consent.redirect_host}.</Typography>
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                                <Button disabled={!!answering} onClick={() => answer(false)}>Cancelar</Button>
                                <LoadingButton variant="contained" loading={answering === "approve"} disabled={!!answering || !companies.length}
                                               onClick={() => answer(true)}>
                                    Autorizar
                                </LoadingButton>
                            </Stack>
                        </Stack>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};

export default OAuthAuthorize;
